// src/component/SearchBar.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useServiceStore } from "../../stores/service.store.ts";
import { userStore } from "../../stores/user.store.ts";
import { ServiceRepository } from "../../repositories/service.repository.ts";

const SUGGESTION_ICON = {
  recommended: "trending_up",
  popular: "local_fire_department",
  synonym: "translate",
};

export default function HomeSearchBar({ q, setQ }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const blurTimer = useRef(null);
  const debounceTimer = useRef(null);

  const recommendedKeywords = useServiceStore((s) => s.recommended_keywords);
  const searchHistories = useServiceStore((s) => s.search_histories);
  const user = userStore((s) => s.user);
  const isLoggedIn = !!user;

  // 검색어 자동완성(typeahead) — 타이핑 250ms 후 추천/인기/다국어 유의어 제안.
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    if (!q?.trim()) {
      setSuggestions([]);
      return;
    }
    debounceTimer.current = setTimeout(async () => {
      const result = await ServiceRepository.fetchSearchSuggestions(q);
      setSuggestions(result);
    }, 250);
    return () => clearTimeout(debounceTimer.current);
  }, [q]);

  const goSearch = (keyword) => {
    const kw = (keyword ?? q).trim();
    if (!kw) return;
    setIsFocused(false);
    navigate(`/search?q=${encodeURIComponent(kw)}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") goSearch();
  };

  const handleFocus = () => {
    clearTimeout(blurTimer.current);
    setIsFocused(true);
  };

  const handleBlur = () => {
    blurTimer.current = setTimeout(() => setIsFocused(false), 150);
  };

  const isTyping = !!q?.trim();
  const showDropdown =
    isFocused &&
    (
      (isTyping && suggestions.length > 0) ||
      (!isTyping && ((isLoggedIn && searchHistories.length > 0) || recommendedKeywords.length > 0))
    );

  return (
    <div className="relative">
      <div className="flex items-stretch gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={t("search.home")}
          className="flex-1 w-full rounded-xl border border-border-tertiary px-4 py-3 outline-none
            focus:border-border-interactive-primary"
        />

        <button
          type="button"
          onClick={() => goSearch()}
          aria-label="Search"
          className="shrink-0 inline-flex items-center justify-center rounded-xl border border-border-tertiary
            bg-white px-4 py-3 hover:bg-bg-secondary active:opacity-80"
        >
          <span className="material-symbols-outlined text-[22px] leading-none">search</span>
        </button>
      </div>

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border-tertiary rounded-xl shadow-lg z-50 overflow-hidden">
          {isTyping ? (
            <div>
              {suggestions.map((s) => (
                <button
                  key={`${s.type}-${s.keyword}`}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { setQ(s.keyword); goSearch(s.keyword); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-body-md text-text-primary hover:bg-bg-secondary text-start"
                >
                  <span className="material-symbols-outlined text-[18px] text-text-tertiary leading-none">
                    {SUGGESTION_ICON[s.type] ?? "search"}
                  </span>
                  <span className="flex-1">{s.keyword}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              {recommendedKeywords.length > 0 && (
                <div>
                  <div className="px-4 pt-3 pb-1">
                    <span className="text-body-sm-sb text-text-secondary">Recommended</span>
                  </div>
                  <div className="flex flex-wrap gap-2 px-4 pb-3">
                    {recommendedKeywords.map((kw) => (
                      <button
                        key={kw}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setQ(kw); goSearch(kw); }}
                        className="inline-flex items-center gap-1 rounded-full border border-border-tertiary px-3 py-1.5
                          text-body-sm text-text-primary hover:bg-bg-secondary"
                      >
                        <span className="material-symbols-outlined text-[16px] leading-none">trending_up</span>
                        {kw}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isLoggedIn && searchHistories.length > 0 && (
                <div>
                  <div className="px-4 pt-3 pb-1 flex items-center justify-between">
                    <span className="text-body-sm-sb text-text-secondary">Recent Searches</span>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => ServiceRepository.deleteSearchHistories()}
                      className="text-body-sm text-text-tertiary hover:text-text-secondary"
                    >
                      Clear all
                    </button>
                  </div>
                  {searchHistories.map((kw) => (
                    <button
                      key={kw}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setQ(kw); goSearch(kw); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-body-md text-text-primary hover:bg-bg-secondary text-start"
                    >
                      <span className="material-symbols-outlined text-[18px] text-text-tertiary leading-none">history</span>
                      <span className="flex-1">{kw}</span>
                      <span
                        role="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => { e.stopPropagation(); ServiceRepository.deleteSearchHistories([kw]); }}
                        className="material-symbols-outlined text-[18px] text-text-tertiary leading-none hover:text-text-secondary"
                      >
                        close
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
